import { useEffect, useState } from "react";
import { Plus, Users, BellRing, Trash2, Search, History, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "../api";
import ImportCsv from "../components/ImportCsv";
import Modal from "../components/Modal";
import TreatmentSelect from "../components/TreatmentSelect";
import { inputCls, labelCls, todayIso, toApiDate, toApiTime } from "../utils/datetime";

const EMPTY = { name: "", age: "", gender: "Female", phone: "", treatment: "", visit_date: todayIso(), visit_time: "", notes: "" };

export default function Patients() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canDelete = ["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "");

  const load = () => api.get("/connect/patients").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/connect/patients", {
        ...form,
        age: form.age === "" ? null : parseInt(form.age, 10),
        visit_date: toApiDate(form.visit_date),
        visit_time: toApiTime(form.visit_time),
      });
      toast.success(`Patient saved — ${r.data.patient_id}`);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the patient");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/connect/patients/${id}`);
      toast.success("Patient removed");
    } catch {
      toast.error("Could not remove the patient");
    }
    load();
  };

  const openHistory = async (p) => {
    try {
      const r = await api.get(`/connect/patients/${p.id}/history`);
      setHistory(r.data);
    } catch {
      toast.error("Could not load patient history");
    }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const r = await api.get(`/connect/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Invoice ${inv.number} downloaded`);
    } catch {
      toast.error("Could not generate the invoice PDF");
    }
  };

  const runRecalls = async () => {
    try {
      const { data } = await api.post("/connect/recalls/run");
      if (data.skipped) toast.info("Recall campaigns are ready but inactive — Twilio WhatsApp keys are not configured yet.");
      else toast.success(`Sent ${data.sent} 6-month recall message(s).`);
    } catch {
      toast.error("Could not run recall campaign");
    }
  };

  const filtered = items.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [p.name, p.patient_id, p.phone, p.treatment].some((v) => String(v || "").toLowerCase().includes(q));
  });

  return (
    <div data-testid="connect-patients" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Patients</h2>
          <p className="text-sm text-slate-500">Every patient gets a unique Patient ID used across appointments, follow-ups and history.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ImportCsv module="patients" onDone={load} testid="patients-import-csv" />
          <button data-testid="patients-recall-run" onClick={runRecalls}
            className="inline-flex items-center gap-2 rounded-full border border-pine/20 px-4 py-2.5 text-xs font-bold text-pine hover:border-gold transition-all">
            <BellRing className="h-4 w-4" /> Send 6-Month Recalls
          </button>
          <button data-testid="patients-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-pine transition-all">
            <Plus className="h-4 w-4" /> Add Patient
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          data-testid="patients-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, Patient ID (PAT-000001) or phone…"
          className="h-11 w-full rounded-xl border border-pine/15 bg-white pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-pine"
        />
      </div>

      {showForm && (
        <form data-testid="patients-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Full Name *</label>
            <input required minLength={2} data-testid="patient-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Age</label>
            <input data-testid="patient-age" type="number" min="0" max="120" inputMode="numeric" placeholder="Age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Gender</label>
            <select data-testid="patient-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className={`mt-1 ${inputCls}`}>
              {["Female", "Male", "Other"].map((g) => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Phone *</label>
            <input required type="tel" inputMode="tel" data-testid="patient-phone" placeholder="+91 …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Visit Date</label>
            <input type="date" data-testid="patient-visit-date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Visit Time</label>
            <input type="time" data-testid="patient-visit-time" value={form.visit_time} onChange={(e) => setForm({ ...form, visit_time: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Treatment</label>
            <div className="mt-1"><TreatmentSelect testid="patient-treatment" value={form.treatment} onChange={(v) => setForm({ ...form, treatment: v })} /></div>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={labelCls}>Notes</label>
            <textarea data-testid="patient-notes" rows={2} placeholder="e.g. Patient experiencing sensitivity in lower molar…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
            <button type="submit" disabled={busy} data-testid="patient-submit" className="h-11 rounded-full bg-teal-brand px-6 text-white text-xs font-bold hover:bg-pine transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Save Patient"}
            </button>
            <p className="self-center text-[11px] text-slate-400">A unique Patient ID (PAT-…) is generated automatically.</p>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`patient-card-${p.id.slice(0, 8)}`} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 hover:-translate-y-0.5 hover:shadow-lift transition-all">
            <div className="flex items-start justify-between gap-2">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-soft text-teal-brand font-display font-semibold">
                {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
              <div className="flex flex-col items-end gap-1">
                {p.patient_id && <span className="rounded-full bg-cream px-2.5 py-1 text-[10px] font-bold text-pine">{p.patient_id}</span>}
                {p.is_new && <span className="rounded-full bg-gold-soft/70 px-2.5 py-1 text-[10px] font-bold text-pine-deep">New</span>}
              </div>
            </div>
            <p className="mt-3 font-bold text-pine-deep">{p.name}</p>
            <p className="text-xs text-slate-500">{p.age ? `${p.age} yrs · ` : ""}{p.gender} · {p.phone}</p>
            {p.notes && <p className="mt-1.5 text-[11px] text-slate-400 italic line-clamp-2">"{p.notes}"</p>}
            <div className="mt-3 pt-3 border-t border-pine/6 text-xs text-slate-500 space-y-1">
              <p>Last visit: <span className="font-semibold text-pine">{p.last_visit}</span></p>
              <p>Visits: <span className="font-semibold text-pine">{p.total_visits}</span>{p.balance > 0 && <span className="text-red-500 font-semibold"> · Due {inr(p.balance)}</span>}</p>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button data-testid={`patient-history-${p.id.slice(0, 8)}`} onClick={() => openHistory(p)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-teal-brand hover:text-pine" aria-label={`History of ${p.name}`}>
                <History className="h-3.5 w-3.5" /> History
              </button>
              {canDelete && (
                <button data-testid={`patient-delete-${p.id.slice(0, 8)}`} onClick={() => remove(p.id)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-500 hover:text-red-600" aria-label={`Delete ${p.name}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="sm:col-span-2 xl:col-span-4 rounded-2xl bg-white border border-pine/8 p-10 text-center text-slate-400 text-sm">
            <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" /> {query ? "No patients match your search." : "No patients yet — add your first patient above."}
          </div>
        )}
      </div>

      {history && (
        <Modal testid="patient-history-modal" wide onClose={() => setHistory(null)}
          title={history.patient.name}
          subtitle={`${history.patient.patient_id || "—"} · ${history.patient.phone} · ${history.patient.treatment || "General"}`}>
          {history.patient.notes && <p className="text-xs text-slate-500 italic">"{history.patient.notes}"</p>}
          <div>
            <p className="text-xs font-bold text-pine/60 mb-2 flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Appointments ({history.appointments.length})</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {history.appointments.map((a) => (
                <div key={a.id} className="rounded-xl border border-pine/8 px-4 py-2.5 text-xs flex flex-wrap justify-between gap-2">
                  <span className="font-bold text-pine-deep">{a.service}</span>
                  <span className="text-slate-500">{a.date} · {a.time}</span>
                  <span className="font-semibold text-teal-brand">{a.status || "Upcoming"}</span>
                </div>
              ))}
              {history.appointments.length === 0 && <p className="text-xs text-slate-400">No appointments yet.</p>}
            </div>
          </div>
          {history.billing && (
            <div data-testid="patient-billing-totals" className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-cream/70 px-4 py-3"><p className="text-[10px] uppercase font-bold text-slate-400">Total Billed</p><p className="font-bold text-pine-deep">{inr(history.billing.total_billed)}</p></div>
              <div className="rounded-xl bg-teal-soft/70 px-4 py-3"><p className="text-[10px] uppercase font-bold text-slate-400">Total Paid</p><p className="font-bold text-teal-brand">{inr(history.billing.total_paid)}</p></div>
              <div className="rounded-xl bg-red-50/80 px-4 py-3"><p className="text-[10px] uppercase font-bold text-slate-400">Outstanding</p><p className="font-bold text-red-500">{inr(history.billing.total_outstanding)}</p></div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold text-pine/60 mb-2">Billing History ({history.invoices.length})</p>
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {history.invoices.map((i) => (
                  <button key={i.id} type="button" data-testid={`patient-invoice-${i.number}`} onClick={() => downloadInvoicePdf(i)}
                    className="w-full text-left rounded-xl border border-pine/8 px-3.5 py-2 text-xs hover:border-teal-brand/40 hover:bg-cream/50 transition-colors">
                    <span className="font-bold text-pine-deep">{i.number}</span> · {i.treatment} · {i.date}
                    <span className="block mt-0.5 text-slate-500">Total <b>{inr(i.total)}</b> · Paid <b className="text-teal-brand">{inr(i.paid_total)}</b> · Balance <b className={i.balance > 0 ? "text-red-500" : "text-slate-400"}>{inr(i.balance)}</b> · {i.payment_status}</span>
                  </button>
                ))}
                {history.invoices.length === 0 && <p className="text-xs text-slate-400">No invoices.</p>}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-pine/60 mb-2">Follow-ups ({history.followups.length})</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {history.followups.map((f) => (
                  <p key={f.id} className="text-xs text-slate-600">{f.treatment || "Follow-up"} · {f.due_date} {f.time || ""} · <span className="font-bold">{f.status}</span></p>
                ))}
                {history.followups.length === 0 && <p className="text-xs text-slate-400">No follow-ups.</p>}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
